<template>
    <div class="self-register">
        <div class="self-register-box">
            <div class="sr-card">
                <div class="sr-header">
                    <div class="sr-langs">
                        <button type="button" class="sr-lang" :class="{active: $store.state.locale === 'hr'}"
                                :title="'Hrvatski'" @click="switchLocale('hr')">
                            <svg class="sr-flag" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
                                <rect width="60" height="10" y="0" fill="#FF0000"></rect>
                                <rect width="60" height="10" y="10" fill="#FFFFFF"></rect>
                                <rect width="60" height="10" y="20" fill="#171796"></rect>
                                <rect x="24" y="9" width="12" height="12" fill="#FFFFFF"></rect>
                                <g fill="#FF0000">
                                    <rect x="24" y="9" width="3" height="3"></rect>
                                    <rect x="30" y="9" width="3" height="3"></rect>
                                    <rect x="27" y="12" width="3" height="3"></rect>
                                    <rect x="33" y="12" width="3" height="3"></rect>
                                    <rect x="24" y="15" width="3" height="3"></rect>
                                    <rect x="30" y="15" width="3" height="3"></rect>
                                    <rect x="27" y="18" width="3" height="3"></rect>
                                    <rect x="33" y="18" width="3" height="3"></rect>
                                </g>
                            </svg>
                        </button>
                        <button type="button" class="sr-lang" :class="{active: $store.state.locale === 'en'}"
                                :title="'English'" @click="switchLocale('en')">
                            <svg class="sr-flag" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
                                <clipPath id="self-register-uk-clip">
                                    <rect width="60" height="30"></rect>
                                </clipPath>
                                <rect width="60" height="30" fill="#012169"></rect>
                                <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" stroke-width="6"></path>
                                <path d="M0,0 L60,30 M60,0 L0,30" clip-path="url(#self-register-uk-clip)"
                                      stroke="#C8102E" stroke-width="4"></path>
                                <path d="M30,0 V30 M0,15 H60" stroke="#FFFFFF" stroke-width="10"></path>
                                <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" stroke-width="6"></path>
                            </svg>
                        </button>
                    </div>
                    <div class="sr-badge"><i class="fa-solid fa-user-plus"></i></div>
                    <h1 class="sr-title">{{ $LANG.Get('labels.selfRegisterTitle') }}</h1>
                    <div v-if="state === 'form' || state === 'password'" class="sr-subtitle">
                        {{ state === 'password' ? $LANG.Get('labels.selfRegisterPasswordPrompt') : $LANG.Get('labels.selfRegisterSubtitle') }}
                    </div>
                </div>

                <div class="sr-body">
                    <div v-if="state === 'loading'" class="sr-state sr-loading">
                        <i class="fa-solid fa-spinner fa-spin"></i>
                        <span>{{ $LANG.Get('labels.selfRegisterLoading') }}</span>
                    </div>

                    <div v-else-if="state === 'disabled'" class="sr-state sr-disabled">
                        <i class="fa-solid fa-circle-info"></i>
                        <span>{{ $LANG.Get('labels.selfRegisterDisabled') }}</span>
                    </div>

                    <div v-else-if="state === 'done'" class="sr-state sr-success">
                        <div class="sr-success-icon"><i class="fa-solid fa-circle-check"></i></div>
                        <div class="sr-success-title">{{ $LANG.Get('labels.selfRegisterSuccessTitle') }}</div>
                        <div class="sr-success-text">{{ $LANG.Get('labels.selfRegisterSuccess') }}</div>
                        <div class="sr-actions">
                            <NevsButton class="primary sr-submit" @click="resetForm()">
                                <i class="fa-solid fa-user-plus"></i> {{ $LANG.Get('buttons.selfRegisterAnother') }}
                            </NevsButton>
                        </div>
                    </div>

                    <template v-else-if="state === 'password'">
                        <NevsTextField v-model="passwordInput" :type="'password'" :error="passwordError"
                                       :label="$LANG.Get('fields.password') + ':'" @keyup.enter="submitPassword()"></NevsTextField>
                        <div class="sr-actions">
                            <NevsButton class="primary sr-submit" @click="submitPassword()">
                                <i class="fa-solid fa-unlock"></i> {{ $LANG.Get('buttons.ok') }}
                            </NevsButton>
                        </div>
                    </template>

                    <template v-else-if="state === 'form'">
                        <div v-if="submitting" class="sr-submitting">
                            <span class="sr-submitting-spinner"><i class="fa-solid fa-spinner fa-spin"></i></span>
                            <span>{{ $LANG.Get('labels.selfRegisterSubmitting') }}</span>
                        </div>
                        <div class="sr-fields">
                            <template v-for="field in fields" :key="field.field">
                                <NevsDateField v-if="field.field === 'date_of_birth'" v-model="registration.date_of_birth"
                                               :error="validation.date_of_birth"
                                               :label="fieldLabel(field) + ':'"></NevsDateField>
                                <NevsSelect v-else-if="field.field === 'gender_id'" :nullable="true" v-model="registration.gender_id"
                                            :options="genders" :error="validation.gender_id"
                                            :label="fieldLabel(field) + ':'"></NevsSelect>
                                <NevsTextField v-else v-model="registration[field.field]" :error="validation[field.field]"
                                               :label="fieldLabel(field) + ':'"></NevsTextField>
                                <!-- Someone without a Croatian OIB enters their own identification number instead;
                                     its format is then not checked and the person is created as foreign. -->
                                <NevsCheckbox v-if="field.field === 'oib'" v-model="registration.foreign"
                                              :label="$LANG.Get('labels.selfRegisterForeign')"
                                              class="sr-foreign"></NevsCheckbox>
                            </template>
                        </div>

                        <div class="sr-actions">
                            <NevsButton class="primary sr-submit" :class="{disabled: submitting}" @click="submit()">
                                <i class="fa-solid fa-paper-plane"></i> {{ $LANG.Get('buttons.send') }}
                            </NevsButton>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";

export default {
    name: "SelfRegister",
    components: {NevsCheckbox, NevsDateField, NevsSelect, NevsTextField, NevsButton},
    data() {
        return {
            state: 'loading',
            link: this.$route.params.link,
            password: '',
            passwordInput: '',
            passwordError: '',
            fields: [],
            genders: [],
            fieldLabels: {
                first_name: 'fields.firstName',
                last_name: 'fields.lastName',
                oib: 'fields.oib',
                gender_id: 'fields.gender',
                date_of_birth: 'fields.dateOfBirth',
                email: 'fields.email',
                phone: 'fields.phone',
                address: 'fields.address',
                postal_code: 'fields.postalCode',
                city: 'fields.city',
                country: 'fields.country'
            },
            registration: this.emptyRegistration(),
            prefillData: null,
            validation: {},
            submitting: false
        }
    },
    methods: {
        emptyRegistration() {
            return {
                first_name: '',
                last_name: '',
                oib: '',
                foreign: false,
                gender_id: null,
                date_of_birth: null,
                email: '',
                phone: '',
                address: '',
                postal_code: '',
                city: '',
                country: ''
            };
        },
        switchLocale(locale) {
            if (this.$store.state.locale === locale) {
                return;
            }
            this.$store.commit('setLocale', locale);
            document.title = this.$LANG.Get('labels.selfRegisterTitle');
        },
        fieldLabel(field) {
            let label = this.fieldLabels[field.field] ? this.$LANG.Get(this.fieldLabels[field.field]) : field.field;
            if (field.field === 'oib' && this.registration.foreign) {
                label = this.$LANG.Get('labels.selfRegisterForeignId');
            }
            return field.mandatory ? '*' + label : label;
        },
        loadConfig(password, passwordAttempt) {
            let vm = this;
            this.$API.APICall('post', 'public/self-register/config/' + encodeURIComponent(this.link), {password: password}, (data, success) => {
                if (!success) {
                    vm.$HELPERS.HandleServerError(vm, data);
                    return;
                }
                if (!data.enabled) {
                    vm.state = 'disabled';
                } else if (data.password_required) {
                    if (passwordAttempt) {
                        vm.passwordError = vm.$LANG.Get('labels.selfRegisterWrongPassword');
                    }
                    vm.state = 'password';
                } else {
                    vm.password = password;
                    vm.fields = data.fields;
                    vm.genders = data.genders;
                    // Kept so the form can be reset for the next person without re-fetching (or re-asking
                    // for the password).
                    vm.prefillData = data;
                    vm.prefill(data);
                    vm.state = 'form';
                }
            });
        },
        prefill(data) {
            // Only prefill fields that are actually shown on the form.
            let visible = new Set(this.fields.map((field) => field.field));
            if (visible.has('country')) {
                this.registration.country = 'Hrvatska';
            }
            if (visible.has('city') && data.city) {
                this.registration.city = data.city;
            }
            if (visible.has('postal_code') && data.postal_code) {
                this.registration.postal_code = data.postal_code;
            }
        },
        submitPassword() {
            this.passwordError = '';
            this.loadConfig(this.passwordInput, true);
        },
        submit() {
            if (this.submitting) {
                return;
            }
            let valid = true;
            this.validation = {};
            let mandatory = this.$LANG.Get('labels.mandatoryField');

            for (let field of this.fields) {
                let key = field.field;
                let value = this.registration[key];
                let empty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
                if (field.mandatory && empty) {
                    this.validation[key] = mandatory;
                    valid = false;
                } else if (key === 'email' && !empty && !this.$HELPERS.CheckEmail(value)) {
                    this.validation[key] = this.$LANG.Get('labels.invalidEmailAddress');
                    valid = false;
                } else if (key === 'oib' && !empty && !this.registration.foreign && !this.$HELPERS.CheckOIB(value)) {
                    this.validation[key] = this.$LANG.Get('labels.invalidOIB');
                    valid = false;
                }
            }

            if (!valid) {
                return;
            }

            let payload = JSON.parse(JSON.stringify(this.registration));
            payload.password = this.password;

            let vm = this;
            this.submitting = true;
            this.$API.APICall('post', 'public/self-register/submit/' + encodeURIComponent(this.link), payload, (data, success) => {
                vm.submitting = false;
                if (success) {
                    vm.state = 'done';
                } else if (data && typeof data === 'object') {
                    for (let key in data) {
                        if (key === 'error') continue;
                        if (data[key] === 'invalid') {
                            vm.validation[key] = (key === 'oib') ? vm.$LANG.Get('labels.invalidOIB') : vm.$LANG.Get('labels.invalidEmailAddress');
                        } else {
                            vm.validation[key] = vm.$LANG.Get('labels.mandatoryField');
                        }
                    }
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        resetForm() {
            // Return to a blank form for the next person. The password (if any) was already validated and is
            // still held in this.password, and the fields/genders/prefill are reused, so no re-fetch or
            // second password prompt is needed.
            this.registration = this.emptyRegistration();
            this.validation = {};
            this.prefill(this.prefillData);
            this.state = 'form';
        }
    },
    mounted() {
        document.title = this.$LANG.Get('labels.selfRegisterTitle');
        this.loadConfig('', false);
    }
}
</script>

<style scoped>
.self-register {
    min-height: 100vh;
    background: linear-gradient(160deg, #FFF5ED 0%, #ffe3cc 55%, #ffd0ad 100%);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 40px 16px;
    box-sizing: border-box;
}

.self-register-box {
    width: 100%;
    max-width: 460px;
}

.sr-card {
    background: #ffffff;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 20px 45px -12px rgba(120, 60, 20, 0.35), 0 4px 12px rgba(120, 60, 20, 0.12);
}

/* ---------- Header ---------- */
.sr-header {
    position: relative;
    background: linear-gradient(135deg, #fb5d18 0%, #ff8a3d 100%);
    color: #ffffff;
    padding: 30px 24px 26px;
    text-align: center;
}

.sr-langs {
    position: absolute;
    top: 14px;
    right: 14px;
    display: flex;
    gap: 6px;
}

.sr-lang {
    padding: 3px;
    width: 34px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.18);
    cursor: pointer;
    opacity: 0.65;
    transition: opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.sr-lang:hover {
    opacity: 1;
    transform: translateY(-1px);
}

.sr-lang.active {
    opacity: 1;
    background: #ffffff;
    border-color: #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.sr-flag {
    width: 24px;
    height: 14px;
    border-radius: 2px;
    display: block;
}

.sr-badge {
    width: 60px;
    height: 60px;
    margin: 0 auto 14px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid rgba(255, 255, 255, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    color: #ffffff;
}

.sr-title {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.2px;
}

.sr-subtitle {
    margin-top: 8px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.4;
}

/* ---------- Body ---------- */
.sr-body {
    position: relative;
    padding: 26px 24px 28px;
}

.sr-submitting {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    background: rgba(255, 255, 255, 0.85);
    color: #a06a45;
    font-size: 16px;
    font-weight: 600;
    animation: sr-fade-in 0.15s ease;
}

.sr-submitting-spinner {
    font-size: 34px;
    color: #fb5d18;
}

.sr-submit.disabled {
    opacity: 0.6;
    pointer-events: none;
}

@keyframes sr-fade-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.sr-fields {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.sr-foreign {
    margin-bottom: 6px;
}

.sr-actions {
    margin-top: 22px;
    text-align: center;
}

.sr-submit {
    padding: 11px 28px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 10px;
}

.sr-submit i {
    margin-right: 6px;
}

/* ---------- States ---------- */
.sr-state {
    text-align: center;
    padding: 16px 8px;
}

.sr-loading {
    color: #a06a45;
    font-size: 16px;
}

.sr-loading i {
    font-size: 24px;
    display: block;
    margin-bottom: 10px;
    color: #fb5d18;
}

.sr-disabled {
    color: #8a7a6c;
    font-size: 16px;
}

.sr-disabled i {
    font-size: 30px;
    display: block;
    margin-bottom: 12px;
    color: #cbb7a5;
}

.sr-success {
    padding: 24px 8px;
}

.sr-success-icon {
    font-size: 52px;
    color: #33a06f;
    line-height: 1;
    margin-bottom: 14px;
    animation: sr-pop 0.35s ease;
}

.sr-success-title {
    font-size: 20px;
    font-weight: 700;
    color: #2f3545;
    margin-bottom: 6px;
}

.sr-success-text {
    font-size: 15px;
    color: #5e6278;
    line-height: 1.5;
}

@keyframes sr-pop {
    0% {
        transform: scale(0.4);
        opacity: 0;
    }
    70% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}
</style>
